import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { debounce } from "lodash";
import { Breadcrumb, Input, Table, Space, Popconfirm, Tooltip } from "antd";
import { Row } from "antd";
import "antd/dist/antd.min.css";
import "../../styles/Main.scss";
import SearchIcon from "../../image/SearchIcon.svg";
import { getRequest, postRequest } from "../../axios";
import { ReactComponent as ArchiveRestore } from "../../image/ArchiveRestore.svg";
import ButtonField from "../../common/htmlComponents/ButtonField";
import {
  ErrorNotificationMsg,
  SuccessNotificationMsg,
} from "../../utils/Notifications";
import { authUserData } from "../../utils/Helper";

const { Search } = Input;

const ArchiveRFQHistory = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ArchiveRFQHistory, setArchiveRFQHistory] = useState([]);
  const VendorId = authUserData()?.vendor ? authUserData()?.vendor : null;
  const [totalCount, settotalCount] = useState(0);
  const [selectedRow, setSelectedRow] = useState([]);
  const [reload, setReload] = useState(false);

  const [state, setState] = useState({
    page: 1,
    ordering: "id",
    pagesize: 10,
    search: "",
    total: 0,
  });

  const categoryId = id;
  useEffect(() => {
    const getArchiveRFQHistory = async () => {
      setLoading(true);

      try {
        let apiResponse = await getRequest(`float-rfq-restore/?vendor_id=${VendorId}`, state);
        if (apiResponse.status) {
          setArchiveRFQHistory(apiResponse.data.results.data);
          settotalCount(apiResponse.data.count);
        } else {
          ErrorNotificationMsg(t("error_in_archive_rfq_history"));
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    getArchiveRFQHistory();
  }, [state, reload, t]);


  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
    setState({
      ...state,
      page: pagination.current,
      pagesize: pagination.pageSize,
      ordering: orderBy,
    });
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "category_tree_name" && sortOrder === "ascend") {
      sortingEnumKey = "category_tree_name";
    }
    if (sortField === "category_tree_name" && sortOrder === "descend") {
      sortingEnumKey = "-category_tree_name";
    }
    if (sortField === "pr_creator" && sortOrder === "ascend") {
      sortingEnumKey = "pr_creator";
    }
    if (sortField === "pr_creator" && sortOrder === "descend") {
      sortingEnumKey = "-pr_creator";
    }
    if (sortField === "pr_number" && sortOrder === "ascend") {
      sortingEnumKey = "pr_number";
    }
    if (sortField === "pr_number" && sortOrder === "descend") {
      sortingEnumKey = "-pr_number";
    }
    if (sortField === "pr_approval_date" && sortOrder === "ascend") {
      sortingEnumKey = "pr_approval_date";
    }
    if (sortField === "pr_approval_date" && sortOrder === "descend") {
      sortingEnumKey = "-pr_approval_date";
    }
    if (sortField === "total_amount" && sortOrder === "ascend") {
      sortingEnumKey = "total_amount";
    }
    if (sortField === "total_amount" && sortOrder === "descend") {
      sortingEnumKey = "-total_amount";
    }
    if (sortField === "rfqstatus" && sortOrder === "ascend") {
      sortingEnumKey = "rfqstatus";
    }
    if (sortField === "rfqstatus" && sortOrder === "descend") {
      sortingEnumKey = "-rfqstatus";
    }

    return sortingEnumKey;
  };

  const searchCallbackDelayed = (event) => {
    const { value } = event.target;
    debounceLoadData(value, onSearch);
  };
  const onSearch = (value) => {
    setState({
      ...state,
      search: value,
    });
  };

  const debounceLoadData = debounce((value, onSearch) => {
    onSearch(value);
  }, 1000);

  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedRow(selectedRowKeys);
    },
  };


  const handleRestore = async (id = "") => {
    let ids = selectedRow;
    if (ids.length === 0) {
      ids.push(id);
    }

    try {
      let apiResponse = await postRequest("float-rfq-restore/", {
        deleted: ids,
      });
      if (apiResponse.data.success) {
        SuccessNotificationMsg(t("rfq_history_restore_successfully"));
        setReload(!reload);
        setSelectedRow([]);
        // navigate(`/request-for-quotation/history`);
        navigate(`/rfq-vendor-request-for-quotation/history/1`);
      } else {
        ErrorNotificationMsg(t("error_in_restore_rfq_history"));
      }
    } catch (error) {
      ErrorNotificationMsg(error);
    }
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "company_name",
      key: "company_name",
      width: 200,
      sorter: true,
    },
    {
      title: "Buyer Name",
      dataIndex: "rfq_approval_by_name",
      key: "rfq_approval_by_name",
      width: 150,
      sorter: true,
    },
    {
      title: "RFQ Number",
      dataIndex: "rfq_number",
      key: "rfq_number",
      width: 150,
      sorter: true,
    },
    {
      title: "City",
      dataIndex: "total_quantity_by_site",
      key: "site_wise_city_name",
      width: 150,
      sorter: true,
      render: (totalQuantityBySite) => {
        if (totalQuantityBySite && totalQuantityBySite.length > 0) {
          const firstSite = totalQuantityBySite[0];
          return firstSite.site_wise_city_name;
        } else {
          return ""; // or handle no city case as needed
        }
      },
    },
    {
      title: "RFQ Due Date",
      dataIndex: "rfq_due_date",
      key: "rfq_due_date",
      sorter: true,
      width: 200,
      render: (text, record) => {
        const rfqDetails = record.rfq_details[0]; 

        return rfqDetails.rfq_due_date;
      },
    },
    {
      title: "RFQ Status",
      dataIndex: "bid_rfq_stage",
      key: "bid_rfq_stage",
      width: 150,
      sorter: true,
      render: (text) => (text ? text : "RFQ Received"), // Checks if `text` exists, otherwise defaults to "RFQ Received"

    },
    // {
    //   title: "Winning Ratio",
    //   dataIndex: "winning_ratio",
    //   key: "winning_ratio",
    //   width: 200,
    //   sorter: true,
    // },
    // {
    //   title: "Winning Percentage",
    //   dataIndex: "winning_percentage",
    //   key: "winning_percentage",
    //   width: 250,
    //   sorter: true,
    // },
    {
      title: "Action",
      key: "operation",
      fixed: "right",
      width: 100,
      render: (text, record) => (
        <Space>
          <Popconfirm
            className="action"
            title={t("pop_restore_title")}
            okText={t("pop_ok")}
            cancelText={t("pop_cancel")}
            onConfirm={() => handleRestore(record.id)}
          >
            <ArchiveRestore className="ArchiverestorImg" />
          </Popconfirm>
        </Space>
      ),
    },
  ];


  const colors = ["#094E8F"];
  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
              <h3>Archived RFQ History - Vendor</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {/* <Link to={`/vendor-request-for-quotation/history/${categoryId}`}>RFQ-History-Vendor</Link> */}
                <Link to={`/rfq-vendor-request-for-quotation/history/1`}>RFQ History - Vendor</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("Archived RFQ History - Vendor")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Space>
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label={t("pl_restore")}
                />
              </Popconfirm>
            )}
          </Space>
        </div>
      </div>

      <section className="grid-sec mt-5 tablegridsec">
        <div className="PR-body">
          <Row className="tblheader">
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-heading">
            <h1>{t("pl_company_address")}</h1>
          </Col> */}
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12} className="tbl-action">
            <div className="searchwrp">
              <Space direction="vertical">
                <Search
                  prefix={
                    <img
                      src={SearchIcon}
                      alt="SearchIcon"
                      className="SearchIcon"
                      width={22}
                    />
                  }
                  placeholder={t("pl_search_list")}
                  onChange={searchCallbackDelayed}
                  style={{
                    width: 250,
                  }}
                />
              </Space>
            </div>
            {selectedRow.length > 0 && (
              <Popconfirm
                className="action"
                title={t("pop_restore_title")}
                okText={t("pop_ok")}
                cancelText={t("pop_cancel")}
                placement="left"
                onConfirm={() => handleRestore()}
              >
                <ButtonField
                  className="BlueShadowBtn"
                  type="primary"
                  htmlType="button"
                  label={t("pl_restore")}
                />
              </Popconfirm>
            )}
          </Col> */}
          </Row>

<div className="tablewrp">
          <Table
            rowSelection={rowSelection}
            columns={columns}
            rowKey={(record) => record.id}
            dataSource={ArchiveRFQHistory}
            loading={loading}
            pagination={{
              defaultCurrent: state.page,
              defaultPageSize: state.pagesize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} items`,
              locale: {
                items_per_page: 'Items / Page', // Text for the size changer
              },
            }}
            onChange={handleTableChange}
          />
          </div>
          <div className="FormFooter Container">
            <ButtonField
              className="CancelGradiantBtn"
              type="primary"
              htmlType="button"
              label={t("previous")}
              onClick={() =>
                navigate(
                  `/rfq-vendor-request-for-quotation/history/1`
                )
              }
            //onClick={() => setCurrentIndex(0)}
            />
          </div>
          
        </div>
      </section>
    </>
  );
};

export default ArchiveRFQHistory;
